export const getDefaultDateRange = () => {
  const today = new Date();
  const previousMonthStart = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    1,
  );
  const previousMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

  return {
    fromDate: formatDateInput(previousMonthStart),
    toDate: formatDateInput(previousMonthEnd),
  };
};

export const formatDateInput = (date) => {
  const localDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60 * 1000,
  );
  return localDate.toISOString().slice(0, 10);
};

export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") {
    return "LKR 0.00";
  }

  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return `LKR ${value}`;
  }

  return `LKR ${amount.toLocaleString("en-LK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatCount = (value) => {
  const number = Number(value);
  if (!Number.isNaN(number)) {
    return number.toLocaleString("en-LK");
  }

  return typeof value === "string" ? value : "0";
};

export const formatPercentage = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return `${value}%`;
  }

  if (typeof value === "string") {
    const percentage = value.trim();
    if (!percentage) {
      return "";
    }
    return percentage.endsWith("%") ? percentage : `${percentage}%`;
  }

  return "";
};

export const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return typeof value === "string" || typeof value === "number"
      ? String(value)
      : "—";
  }

  return date.toLocaleDateString("en-LK", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const formatTime = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleTimeString("en-LK", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  const timeParts = String(value).match(/(\d{1,2}):(\d{2})/);
  if (!timeParts) {
    return typeof value === "string" || typeof value === "number"
      ? String(value)
      : "—";
  }

  const hour = Number(timeParts[1]);
  const minute = timeParts[2];
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute} ${suffix}`;
};

export const getPersonName = (person, fallback) => {
  if (typeof person === "string" && person.trim()) {
    return person;
  }

  if (!person || typeof person !== "object") {
    return fallback || "—";
  }

  if (typeof person.name === "string" && person.name.trim()) {
    return person.name;
  }

  if (typeof person.full_name === "string" && person.full_name.trim()) {
    return person.full_name;
  }

  const combinedName = [person.first_name, person.last_name]
    .filter((name) => typeof name === "string" && name.trim())
    .join(" ");

  return combinedName || fallback || "—";
};

export const getDoctorId = (doctorRecord, index) => {
  const record =
    doctorRecord && typeof doctorRecord === "object" ? doctorRecord : {};
  const doctor =
    record.doctor && typeof record.doctor === "object" ? record.doctor : {};
  return String(
    doctor.id ||
      doctor.doctor_id ||
      record.doctor_id ||
      record.id ||
      `doctor-${index}`,
  );
};

const pad2 = (value) => String(value).padStart(2, "0");

/**
 * Split a doctor's consultations into calendar months (by consultation date),
 * oldest month first. Each month's from/to is clamped to the selected range so
 * a payslip for a partial first/last month only covers the filtered days.
 */
export const groupConsultationsByMonth = (consultations, dateRange) => {
  const range = dateRange || {};
  const rangeFrom = range.from_date || range.from || "";
  const rangeTo = range.to_date || range.to || "";
  const months = {};

  (Array.isArray(consultations) ? consultations : []).forEach((consultation) => {
    const match = String(consultation.date || "").match(/^(\d{4})-(\d{2})/);
    const key = match ? `${match[1]}-${match[2]}` : "unknown";
    if (!months[key]) {
      months[key] = { key, consultations: [] };
    }
    months[key].consultations.push(consultation);
  });

  return Object.keys(months)
    .sort()
    .map((key) => {
      const month = months[key];
      const monthConsultations = month.consultations;
      let label = "Unknown month";
      let fromDate = rangeFrom;
      let toDate = rangeTo;

      if (key !== "unknown") {
        const [year, monthNumber] = key.split("-").map(Number);
        const lastDay = new Date(year, monthNumber, 0).getDate();
        const monthStart = `${key}-01`;
        const monthEnd = `${key}-${pad2(lastDay)}`;
        label = new Date(year, monthNumber - 1, 1).toLocaleDateString("en-GB", {
          month: "long",
          year: "numeric",
        });
        fromDate = rangeFrom && rangeFrom > monthStart ? rangeFrom : monthStart;
        toDate = rangeTo && rangeTo < monthEnd ? rangeTo : monthEnd;
      }

      const isPaid = (consultation) =>
        String(consultation.payment_status || "").toLowerCase() === "paid";
      const sum = (field) =>
        monthConsultations.reduce(
          (total, consultation) => total + (Number(consultation[field]) || 0),
          0,
        );

      return {
        key,
        label,
        dateRange: { from_date: fromDate, to_date: toDate },
        consultations: monthConsultations,
        grossFees: sum("gross_fees"),
        netPayment: sum("doctor_net_payment"),
        paidCount: monthConsultations.filter(isPaid).length,
        unpaidCount: monthConsultations.filter(
          (consultation) => consultation.payment_id && !isPaid(consultation),
        ).length,
        paidPaymentIds: Array.from(
          new Set(
            monthConsultations
              .filter((consultation) => consultation.payment_id && isPaid(consultation))
              .map((consultation) => Number(consultation.payment_id)),
          ),
        ),
      };
    });
};

export const getDateRangeLabel = (dateRange, fallbackRange) => {
  if (typeof dateRange === "string" && dateRange.trim()) {
    return dateRange;
  }

  const range = dateRange || {};
  const from =
    range.from_date || range.from || range.start_date || range.start;
  const to = range.to_date || range.to || range.end_date || range.end;

  if (from || to) {
    return `${formatDate(from)} – ${formatDate(to)}`;
  }

  if (fallbackRange && (fallbackRange.fromDate || fallbackRange.toDate)) {
    return `${formatDate(fallbackRange.fromDate)} – ${formatDate(
      fallbackRange.toDate,
    )}`;
  }

  return "—";
};

export const getValidationMessages = (error) => {
  const responseData =
    (error && error.response && error.response.data) || {};
  const errors = responseData.errors || (responseData.data && responseData.data.errors);

  if (Array.isArray(errors)) {
    return errors.filter(Boolean).map(String);
  }

  if (errors && typeof errors === "object") {
    return Object.keys(errors).reduce((messages, key) => {
      const value = errors[key];
      if (Array.isArray(value)) {
        return messages.concat(value.map(String));
      }
      if (value) {
        messages.push(String(value));
      }
      return messages;
    }, []);
  }

  return [];
};

export const getPageNumbers = (currentPage, lastPage) => {
  const windowSize = 5;
  if (lastPage <= windowSize) {
    return Array.from({ length: lastPage }, (_, index) => index + 1);
  }

  let start = Math.max(1, currentPage - Math.floor(windowSize / 2));
  let end = start + windowSize - 1;
  if (end > lastPage) {
    end = lastPage;
    start = lastPage - windowSize + 1;
  }

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};
